/**
 * Settings modal - User preferences for display, currency, and theme
 */
import clsx from 'clsx';
import { useState, type ReactElement } from 'react';
import { IoCash, IoColorPalette, IoGrid, IoLibrary, IoSettings, IoWarning } from 'react-icons/io5';

import Dropdown from '@/components/Dropdown/Dropdown';
import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';
import { CONDITION_OPTIONS, LANGUAGE_OPTIONS } from '@/lib/cardUtils';
import { CURRENCY_LABELS, THEME_LABELS, VIEW_MODE_LABELS } from '@/lib/settingsUtils';
import { clearAllCollectionCards } from '@/services/collection';
import { clearAllDecks } from '@/services/decks';
import { clearAllFolders } from '@/services/folders';
import { clearFullCardCache } from '@/services/indexedDbCache';
import type { Condition, Currency, Language, Theme, ViewMode } from '@/types/card';
import useUserSettings from '../hooks/api';

export interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SettingGroupProps {
  icon: ReactElement;
  title: string;
  description: string;
  children: ReactElement;
}

function SettingGroup({ icon, title, description, children }: SettingGroupProps): ReactElement {
  return (
    <div className="border-storm border-b pb-6 last:border-b-0 last:pb-0">
      <div className="flex items-start gap-4">
        <div className="bg-slate text-aether mt-0.5 rounded-lg p-2">{icon}</div>
        <div className="flex-1">
          <h3 className="text-pearl font-medium">{title}</h3>
          <p className="text-ash mt-0.5 text-sm">{description}</p>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

interface OptionButtonProps {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}

function OptionButton({ selected, onClick, children, disabled }: OptionButtonProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
        selected ? 'bg-aether text-void' : 'bg-slate text-silver hover:bg-storm hover:text-pearl',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {children}
    </button>
  );
}

interface DangerButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
}

function DangerButton({ onClick, children, disabled, isLoading }: DangerButtonProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={clsx(
        'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
        'bg-red-900/30 text-red-400 hover:bg-red-900/50 hover:text-red-300',
        'border border-red-900/50',
        (disabled || isLoading) && 'cursor-not-allowed opacity-50',
      )}
    >
      {isLoading && <Spinner size="sm" />}
      {children}
    </button>
  );
}

interface DeveloperSectionProps {
  isUpdating: boolean;
}

interface ClearProgress {
  label: string;
  cleared: number;
  total: number;
}

function ProgressOverlay({ progress }: { progress: ClearProgress }): ReactElement {
  const indeterminate = progress.total === 0;
  const percent = indeterminate ? 0 : Math.round((progress.cleared / progress.total) * 100);

  return (
    <div className="bg-void/80 absolute inset-0 z-10 flex items-center justify-center rounded-xl backdrop-blur-sm">
      <div className="bg-slate w-72 rounded-lg p-6 shadow-xl">
        <p className="text-pearl mb-3 text-sm font-medium">{progress.label}</p>
        <div className="bg-storm h-3 overflow-hidden rounded-full">
          {indeterminate ? (
            <div className="bg-aether h-full w-1/3 animate-pulse rounded-full" />
          ) : (
            <div className="bg-aether h-full rounded-full transition-all duration-200" style={{ width: `${percent}%` }} />
          )}
        </div>
        <p className="text-ash mt-2 text-xs">{indeterminate ? 'Preparing…' : `${percent}%`}</p>
      </div>
    </div>
  );
}

function DeveloperSection({ isUpdating }: DeveloperSectionProps): ReactElement {
  const [isClearing, setIsClearing] = useState<'collection' | 'folders' | 'decks' | 'all' | 'cache' | null>(null);
  const [confirmAction, setConfirmAction] = useState<'collection' | 'folders' | 'decks' | 'all' | 'cache' | null>(null);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [progress, setProgress] = useState<ClearProgress | null>(null);

  const handleClearCollection = async () => {
    setIsClearing('collection');
    setResult(null);
    setProgress({ label: 'Clearing collection cards…', cleared: 0, total: 0 });
    try {
      const count = await clearAllCollectionCards((cleared, total) => {
        setProgress({ label: 'Clearing collection cards…', cleared, total });
      });
      setResult({ type: 'success', message: `Cleared ${count} cards from collection` });
    } catch (error) {
      setResult({ type: 'error', message: 'Failed to clear collection' });
      console.error('Failed to clear collection:', error);
    } finally {
      setIsClearing(null);
      setConfirmAction(null);
      setProgress(null);
    }
  };

  const handleClearFolders = async () => {
    setIsClearing('folders');
    setResult(null);
    setProgress({ label: 'Clearing folders…', cleared: 0, total: 0 });
    try {
      const count = await clearAllFolders((cleared, total) => {
        setProgress({ label: 'Clearing folders…', cleared, total });
      });
      setResult({ type: 'success', message: `Cleared ${count} folders` });
    } catch (error) {
      setResult({ type: 'error', message: 'Failed to clear folders' });
      console.error('Failed to clear folders:', error);
    } finally {
      setIsClearing(null);
      setConfirmAction(null);
      setProgress(null);
    }
  };

  const handleClearDecks = async () => {
    setIsClearing('decks');
    setResult(null);
    setProgress({ label: 'Clearing decks…', cleared: 0, total: 0 });
    try {
      const count = await clearAllDecks((cleared, total) => {
        setProgress({ label: 'Clearing decks…', cleared, total });
      });
      setResult({ type: 'success', message: `Cleared ${count} decks and all deck cards` });
    } catch (error) {
      setResult({ type: 'error', message: 'Failed to clear decks' });
      console.error('Failed to clear decks:', error);
    } finally {
      setIsClearing(null);
      setConfirmAction(null);
      setProgress(null);
    }
  };

  const handleClearAll = async () => {
    setIsClearing('all');
    setResult(null);
    setProgress({ label: 'Preparing…', cleared: 0, total: 0 });
    try {
      let totalCleared = 0;
      let totalItems = 0;

      const cardCount = await clearAllCollectionCards((cleared, total) => {
        if (cleared === 0) totalItems += total;
        setProgress({ label: 'Clearing collection cards…', cleared: totalCleared + cleared, total: totalItems });
      });
      totalCleared += cardCount;

      const folderCount = await clearAllFolders((cleared, total) => {
        if (cleared === 0) totalItems += total;
        setProgress({ label: 'Clearing folders…', cleared: totalCleared + cleared, total: totalItems });
      });
      totalCleared += folderCount;

      const deckCount = await clearAllDecks((cleared, total) => {
        if (cleared === 0) totalItems += total;
        setProgress({ label: 'Clearing decks…', cleared: totalCleared + cleared, total: totalItems });
      });

      setResult({ type: 'success', message: `Cleared ${cardCount} cards, ${folderCount} folders, and ${deckCount} decks` });
    } catch (error) {
      setResult({ type: 'error', message: 'Failed to clear data' });
      console.error('Failed to clear all data:', error);
    } finally {
      setIsClearing(null);
      setConfirmAction(null);
      setProgress(null);
    }
  };

  const handleClearCache = async () => {
    setIsClearing('cache');
    setResult(null);
    try {
      await clearFullCardCache();
      setResult({ type: 'success', message: 'Cleared IndexedDB card cache. Reload the page to refetch card data.' });
    } catch (error) {
      setResult({ type: 'error', message: 'Failed to clear card cache' });
      console.error('Failed to clear card cache:', error);
    } finally {
      setIsClearing(null);
      setConfirmAction(null);
    }
  };

  const handleConfirm = () => {
    if (confirmAction === 'collection') handleClearCollection();
    else if (confirmAction === 'folders') handleClearFolders();
    else if (confirmAction === 'decks') handleClearDecks();
    else if (confirmAction === 'all') handleClearAll();
    else if (confirmAction === 'cache') handleClearCache();
  };

  const getConfirmMessage = () => {
    switch (confirmAction) {
      case 'collection':
        return 'This will permanently delete ALL cards in your collection.';
      case 'folders':
        return 'This will permanently delete ALL folders. Cards will become unfiled.';
      case 'decks':
        return 'This will permanently delete ALL decks and ALL cards within those decks.';
      case 'all':
        return 'This will permanently delete ALL cards, folders, and decks.';
      case 'cache':
        return 'This will clear the local IndexedDB card cache. Card data will be refetched on next page load.';
      default:
        return '';
    }
  };

  return (
    <div className="border-storm relative border-t pt-6">
      {progress && <ProgressOverlay progress={progress} />}
      <div className="flex items-start gap-4">
        <div className="mt-0.5 rounded-lg bg-red-900/30 p-2 text-red-400">
          <IoWarning className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-pearl font-medium">Developer Tools</h3>
          <p className="text-ash mt-0.5 text-sm">For testing purposes. These actions cannot be undone.</p>

          {/* Result message */}
          {result && (
            <div
              className={clsx(
                'mt-4 rounded-lg px-3 py-2 text-sm',
                result.type === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400',
              )}
            >
              {result.message}
            </div>
          )}

          {/* Confirmation dialog */}
          {confirmAction && (
            <div className="bg-slate mt-4 rounded-lg p-4">
              <p className="text-pearl text-sm font-medium">Are you sure?</p>
              <p className="text-ash mt-1 text-sm">{getConfirmMessage()}</p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isClearing !== null}
                  className={clsx(
                    'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium',
                    'bg-red-600 text-white hover:bg-red-500',
                    isClearing !== null && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {isClearing !== null && <Spinner size="sm" />}
                  Yes, delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmAction(null)}
                  disabled={isClearing !== null}
                  className="bg-storm text-silver hover:bg-storm/70 rounded-lg px-4 py-2 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!confirmAction && (
            <div className="mt-4 flex flex-wrap gap-2">
              <DangerButton onClick={() => setConfirmAction('collection')} disabled={isUpdating} isLoading={isClearing === 'collection'}>
                Clear Collection
              </DangerButton>
              <DangerButton onClick={() => setConfirmAction('folders')} disabled={isUpdating} isLoading={isClearing === 'folders'}>
                Clear Folders
              </DangerButton>
              <DangerButton onClick={() => setConfirmAction('decks')} disabled={isUpdating} isLoading={isClearing === 'decks'}>
                Clear Decks
              </DangerButton>
              <DangerButton onClick={() => setConfirmAction('all')} disabled={isUpdating} isLoading={isClearing === 'all'}>
                Clear All Data
              </DangerButton>
              <DangerButton onClick={() => setConfirmAction('cache')} disabled={isUpdating} isLoading={isClearing === 'cache'}>
                Clear Card Cache
              </DangerButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SettingsModal({ isOpen, onClose }: SettingsModalProps): ReactElement {
  const { settings, isLoading, updateSettings, isUpdating } = useUserSettings();

  const handleCurrencyChange = (currency: Currency) => {
    updateSettings({ currency });
  };

  const handleViewModeChange = (defaultView: ViewMode) => {
    updateSettings({ defaultView });
  };

  const handleDefaultConditionChange = (defaultCondition: Condition) => {
    updateSettings({ defaultCondition });
  };

  const handleDefaultLanguageChange = (defaultLanguage: Language) => {
    updateSettings({ defaultLanguage });
  };

  const handleThemeChange = (theme: Theme) => {
    updateSettings({ theme });
  };

  const handleCompactTableChange = (compactTable: boolean) => {
    updateSettings({ compactTable });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Settings" size="md">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Currency */}
          <SettingGroup
            icon={<IoCash className="h-5 w-5" />}
            title="Currency"
            description="Choose your preferred currency for price display"
          >
            <div className="flex gap-2">
              {(Object.entries(CURRENCY_LABELS) as [Currency, string][]).map(([value, label]) => (
                <OptionButton
                  key={value}
                  selected={settings.currency === value}
                  onClick={() => handleCurrencyChange(value)}
                  disabled={isUpdating}
                >
                  {label}
                </OptionButton>
              ))}
            </div>
          </SettingGroup>

          {/* Collection Defaults */}
          <SettingGroup
            icon={<IoLibrary className="h-5 w-5" />}
            title="Collection Defaults"
            description="Default condition and language when adding cards to your collection"
          >
            <div className="flex items-end gap-4">
              <div className="w-44">
                <label htmlFor="settings-condition" className="text-silver mb-1.5 block text-sm font-medium">
                  Condition
                </label>
                <Dropdown
                  id="settings-condition"
                  value={settings.defaultCondition}
                  options={CONDITION_OPTIONS}
                  onChange={(v) => handleDefaultConditionChange((v ?? 'NM') as Condition)}
                  disabled={isUpdating}
                  aria-label="Default condition"
                />
              </div>
              <div className="w-44">
                <label htmlFor="settings-language" className="text-silver mb-1.5 block text-sm font-medium">
                  Language
                </label>
                <Dropdown
                  id="settings-language"
                  value={settings.defaultLanguage}
                  options={LANGUAGE_OPTIONS}
                  onChange={(v) => handleDefaultLanguageChange((v ?? 'en') as Language)}
                  disabled={isUpdating}
                  aria-label="Default language"
                />
              </div>
            </div>
          </SettingGroup>

          {/* Default View */}
          <SettingGroup
            icon={<IoGrid className="h-5 w-5" />}
            title="Default View"
            description="Choose your preferred default view for the collection"
          >
            <div className="flex gap-2">
              {(Object.entries(VIEW_MODE_LABELS) as [ViewMode, string][]).map(([value, label]) => (
                <OptionButton
                  key={value}
                  selected={settings.defaultView === value}
                  onClick={() => handleViewModeChange(value)}
                  disabled={isUpdating}
                >
                  {label}
                </OptionButton>
              ))}
            </div>
          </SettingGroup>

          {/* Compact Table */}
          <SettingGroup
            icon={<IoSettings className="h-5 w-5" />}
            title="Compact Table"
            description="Use compact mode in table view by default (hides images, reduces padding)"
          >
            <div className="flex gap-2">
              <OptionButton selected={!settings.compactTable} onClick={() => handleCompactTableChange(false)} disabled={isUpdating}>
                Normal
              </OptionButton>
              <OptionButton selected={settings.compactTable} onClick={() => handleCompactTableChange(true)} disabled={isUpdating}>
                Compact
              </OptionButton>
            </div>
          </SettingGroup>

          {/* Theme */}
          <SettingGroup icon={<IoColorPalette className="h-5 w-5" />} title="Theme" description="Choose your preferred color theme">
            <div className="flex gap-2">
              {(Object.entries(THEME_LABELS) as [Theme, string][]).map(([value, label]) => (
                <OptionButton
                  key={value}
                  selected={settings.theme === value}
                  onClick={() => handleThemeChange(value)}
                  disabled={isUpdating}
                >
                  {label}
                </OptionButton>
              ))}
            </div>
          </SettingGroup>

          {/* Developer Tools - only in development */}
          {import.meta.env.DEV && <DeveloperSection isUpdating={isUpdating} />}
        </div>
      )}
    </Modal>
  );
}
